import _ from 'lodash'
import Backbone from 'backbone'
import i18n from 'scoreboard/components/i18n'
import rangeToPeriods from 'scoreboard/reports/common/range-to-periods'
import calendars from 'scoreboard/components/calendars'
import globals from 'scoreboard/components/globals'

/**
 * Client model of the breakdown in the report
 */
class ReportBreakdown extends Backbone.Model {

	defaults() {
		return {
			breakdownLabelsWithId: []
		}
	}

	initialize(options = {}) {
		this.on('change:range', (model, range, opts) => {
			if (range && range.first != range.last) {
				//if we're not longer showing a single period, do not hide label
				this.unset('hideLabel', {silent: true})
			}
			this.ensureValidStaticSortDate()
		})
	}

	validate(attrs = this.attributes, opts) {
		let subtotalError = this.validateSubtotalCalendars()
		if (subtotalError) {
			return subtotalError
		}
		return null
	}

	warnings() {
		let warnings = []
		if (this.attributes.exceedsLabelLimit) {
			warnings.push(i18n.get('report.designer.repeatByExceedsLimit.singular'))
		}
		return warnings
	}

	isBreakingDownWithColumn() {
		return !_.isNil(this.attributes.breakdownBy)
	}

	isTimeBasedBreakdown(reportModel) {
		if (!_.isNil(this.attributes.range) && _.isNil(this.attributes.breakdownBy)) {
			return true
		}
		return reportModel.columnDataByFieldIdOrColumnType(this.attributes.breakdownBy)
			.changesOverTime
	}

	/**
	 * If the sort is against a static date range, this method ensure the period
	 * selected follows the rules outlined here
	 * https://github.com/SpiderStrategies/Scoreboard/issues/42669#issuecomment-983955794
	 */
	ensureValidStaticSortDate() {
		let range = this.get('range') || {}
		let sortByValue = this.get('sortByValue')
		if (!range.relative && !_.isNil(sortByValue)) {
			let periods = rangeToPeriods(range)
			//https://github.com/SpiderStrategies/Scoreboard/issues/42669#issuecomment-983790969
			//we need to determine if the static period still exists,
			//if not then remove sort
			if (_.findIndex(periods, (p) => p.id == sortByValue) == -1) {
				this.unset('sortByValue', {removeSort: true})
				this.unset('sortByColumnIndex',  {silent: true})
			}
		}
	}

	validateSubtotalCalendars() {
		let currentCalendar = this.currentCalendar()
		if (!currentCalendar) return null

		if (_.some(this.get('subtotalCalendarIds'), subtotalCalId => {
			let subtotalCalendar = calendars.calendarsById[subtotalCalId]
			return subtotalCalendar.avgPeriodSize <= currentCalendar.avgPeriodSize
		})) {
			return i18n.get('report.designer.invalidSubtotalCalendar')
		}
		return null
	}

	validSubtotalCalendarIds() {
		let currentCalendar = this.currentCalendar()
		if ( !currentCalendar ) { return [] }

		let currentSubTotalCalendarIds = _.keyBy(this.get('subtotalCalendarIds') || [])
		let c = _.filter(calendars.calendarsById, (cal, calId) => {
			return !currentSubTotalCalendarIds[calId] && cal.avgPeriodSize > currentCalendar.avgPeriodSize
		})
		return _.map(c, 'id')
	}

	currentCalendar() {
		let range = this.get('range')
		if (!range) { return null }
		if (range.relative && _.isNil(range.calendarId)) {
			return  _.get(calendars.periodsById[globals.periodId], 'calendar', null)
		} else {
			return calendars.calendarsById[range.calendarId]
		}
	}

	addSubtotalCalendarId(ids, opts = {}) {
		let subtotalCalendarIds = this.get('subtotalCalendarIds') || []
		if (!_.isArray(ids)) {
			ids = [ids]
		}
		this.set('subtotalCalendarIds', subtotalCalendarIds.concat(ids), opts)
	}

	removeSubtotalCalendarId(ids, opts = {}) {
		let subtotalCalendarIds = this.get('subtotalCalendarIds') || []
		if (!_.isArray(ids)) {
			ids = [ids]
		}
		this.set('subtotalCalendarIds', _.without(subtotalCalendarIds, ...ids), opts)
	}

	//no-op because we're using this solely as a nested model
	sync() {}
}

export default ReportBreakdown
ReportBreakdown.__filename__ = 'reports/common/model/designer-breakdown.js'
